import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type { MeResponse, OracleAskResponse, OracleHistoryItem, OracleHistoryResponse } from '../../contracts/api'
import { haptic } from '../telegram'
import './oracle.css'

const METHODS = [
  { id: 'coins', title: 'Монеты', note: 'три монеты, шесть бросков' },
  { id: 'numbers', title: 'Два числа', note: 'называешь два числа' },
  { id: 'moment', title: 'Момент вопроса', note: 'считается по времени, когда спросил' },
]

const ORDINALS = ['первую', 'вторую', 'третью', 'четвёртую', 'пятую', 'шестую']

type Stage = 'idle' | 'question' | 'method' | 'ritual' | 'reading'

export function Oracle(_props: { me: MeResponse }) {
  const [stage, setStage] = useState<Stage>('idle')
  const [question, setQuestion] = useState('')
  const [method, setMethod] = useState('coins')
  const [numbers, setNumbers] = useState({ first: '', second: '' })
  const [answer, setAnswer] = useState<OracleAskResponse | null>(null)
  const [shown, setShown] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [history, setHistory] = useState<OracleHistoryItem[]>([])
  const [freeLeft, setFreeLeft] = useState(1)

  useEffect(() => {
    api
      .get<OracleHistoryResponse>('/api/oracle/history')
      .then((data) => {
        setHistory(data.questions)
        setFreeLeft(data.freeLeft)
      })
      .catch(() => undefined)
  }, [])

  async function ask(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      const result = await api.post<OracleAskResponse>('/api/oracle/ask', {
        question,
        method,
        ...(method === 'numbers' ? { first: Number(numbers.first), second: Number(numbers.second) } : {}),
      })
      setAnswer(result)
      setShown(0)
      setStage('ritual')
      haptic('medium')
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не получилось задать вопрос')
    } finally {
      setBusy(false)
    }
  }

  if (stage === 'idle') {
    return (
      <div className="screen oracle">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">оракул</div>
        </div>
        <h1 className="display">Вопрос</h1>
        <div className="sub">
          Шесть линий появляются по одной. Ответ не «да» и не «нет» — это описание того, как устроена
          ситуация. Перебросить один и тот же вопрос нельзя.
        </div>
        <button type="button" className="btn btn-gold cta" onClick={() => setStage('question')}>
          Задать вопрос
        </button>
        {freeLeft === 0 && <div className="oracle-note">Бесплатный вопрос уже задан. Дальше — по подписке.</div>}

        {history.length > 0 && (
          <>
            <div className="lbl sect">История</div>
            <div className="rowlist">
              {history.map((item) => (
                <div className="rowitem" key={item.id}>
                  <div>
                    <div className="ttl">{item.question}</div>
                    <div className="note">
                      гексаграмма {item.castData.number}
                      {item.returnOn ? ` · вернуться ${item.returnOn}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  if (stage === 'question') {
    return (
      <div className="screen oracle">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">оракул</div>
        </div>
        <h1 className="display">О чём спрашиваешь</h1>
        <div className="sub">Одна конкретная ситуация. Не «что меня ждёт», а «стоит ли переходить в этот проект».</div>
        <textarea
          className="field oracle-input"
          rows={3}
          maxLength={300}
          placeholder="Переходить ли мне в новый проект этой осенью?"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <button
          type="button"
          className="btn btn-gold cta"
          disabled={question.trim().length < 8}
          onClick={() => setStage('method')}
        >
          Дальше
        </button>
      </div>
    )
  }

  if (stage === 'method') {
    return (
      <div className="screen oracle">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">оракул</div>
        </div>
        <div className="q">
          <div className="quote">«{question}»</div>
        </div>
        <div className="lbl sect">Как строим</div>
        <div className="rowlist">
          {METHODS.map((item) => (
            <button
              type="button"
              className={`rowitem${method === item.id ? ' picked' : ''}`}
              key={item.id}
              onClick={() => setMethod(item.id)}
            >
              <div>
                <div className="ttl">{item.title}</div>
                <div className="note">{item.note}</div>
              </div>
              <div className="right">{method === item.id ? '✓' : ''}</div>
            </button>
          ))}
        </div>
        {method === 'numbers' && (
          <div className="numbers-row">
            <input
              className="field"
              inputMode="numeric"
              placeholder="Первое число"
              value={numbers.first}
              onChange={(event) => setNumbers({ ...numbers, first: event.target.value.replace(/\D/g, '') })}
            />
            <input
              className="field"
              inputMode="numeric"
              placeholder="Второе"
              value={numbers.second}
              onChange={(event) => setNumbers({ ...numbers, second: event.target.value.replace(/\D/g, '') })}
            />
          </div>
        )}
        <button
          type="button"
          className="btn btn-gold cta"
          disabled={busy || (method === 'numbers' && (!numbers.first || !numbers.second))}
          onClick={() => void ask()}
        >
          {busy ? 'Строим…' : 'Построить'}
        </button>
        {error && <div className="field-warn">{error}</div>}
      </div>
    )
  }

  if (!answer) return <div className="state">…</div>

  const allShown = shown >= 6

  return (
    <div className="screen oracle">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">оракул</div>
      </div>
      <div className="back">И-ЦЗИН · МЭЙ ХУА</div>

      <div className="q">
        <div className="quote">«{answer.question}»</div>
        <div className="meta">
          метод — {METHODS.find((item) => item.id === answer.method)?.title.toLowerCase() ?? answer.method}
        </div>
      </div>

      <div className="ritual">
        <div className="glow" />
        <div className="lines">
          {answer.cast.lines.map((line, index) => {
            const visible = index < shown
            const classes = ['hexline']
            if (!visible) classes.push('empty')
            else classes.push(line.yang ? 'yang' : 'yin')
            if (visible && line.changing) classes.push('chg')
            return (
              <div className={classes.join(' ')} key={index}>
                <span className="num">{index + 1}</span>
                {!visible && <span className="seg full" />}
                {visible && line.yang && <span className="seg" />}
                {visible && !line.yang && (
                  <>
                    <span className="seg a" />
                    <span className="seg b" />
                  </>
                )}
                {visible && line.changing && <span className="tag">меняющаяся</span>}
              </div>
            )
          })}
        </div>
      </div>

      {!allShown ? (
        <>
          <div className="counter">
            <div className="c1">
              {shown === 0 ? 'Ни одной черты' : `${['Одна', 'Две', 'Три', 'Четыре', 'Пять'][shown - 1]} черт${shown === 1 ? 'а' : shown < 5 ? 'ы' : ''} из шести`}
            </div>
            <div className="c2">
              {shown === 0
                ? 'Черты открываются по одной — снизу вверх, как и положено.'
                : 'Не торопись. Узор ещё собирается.'}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost cta"
            onClick={() => {
              haptic('light')
              setShown((current) => current + 1)
            }}
          >
            Открыть {ORDINALS[shown]} черту
          </button>
        </>
      ) : (
        <div className="reading">
          <div className="card">
            <div className="lbl" style={{ color: 'var(--green)' }}>
              Гексаграмма {answer.cast.number} · <span className="cjk">{answer.cast.nameZh}</span>
            </div>
            <p className="reading-short">{answer.reading?.short ?? answer.library.short}</p>
          </div>

          {answer.library.lines.map((line) => (
            <div className="card reading-line" key={line.line}>
              <div className="lbl">Меняющаяся линия {line.line}</div>
              <p>{line.meaning}</p>
              <p className="reading-advice">{line.advice}</p>
            </div>
          ))}

          <div className="card">
            <div className="lbl">Глубже</div>
            <p>{answer.reading?.depth ?? answer.library.depth}</p>
          </div>

          {answer.cast.changingLines.length > 0 && (
            <div className="card">
              <div className="lbl">Во что превращается</div>
              <p>
                Гексаграмма {answer.cast.transformed.number} · <span className="cjk">{answer.cast.transformed.nameZh}</span>
              </p>
            </div>
          )}

          <div className="card reading-question">
            <div className="lbl" style={{ color: 'var(--gold)' }}>
              Вопрос к себе
            </div>
            <p>{answer.reading?.question ?? answer.library.question}</p>
          </div>

          <button type="button" className="btn btn-ghost cta" onClick={() => setStage('idle')}>
            Сохранить и закрыть
          </button>
        </div>
      )}
    </div>
  )
}
